import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import api from 'api/api';
import useVersionInfo from 'hooks/useVersionInfo';

import { V4NodeScoresResponse } from './types';

// The server hard-422s on the 51st id in a single request, so chunk below it.
export const NODE_SCORES_CHUNK_SIZE = 50;

// The endpoint 422s on composite `${uuid}#${path}` ids.
export const normalizeNodeIds = (ids: string[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of ids) {
    const bare = id?.split('#')[0];
    if (!bare || seen.has(bare)) continue;
    seen.add(bare);
    out.push(bare);
  }
  return out;
};

export const chunk = <T>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) chunks.push(items.slice(i, i + size));
  return chunks;
};

type FetchNodeScoresParams = {
  version: string;
  system_model: string;
  indicatorId: string;
  nodeIds: string[];
};

export const fetchNodeScores = async ({
  version,
  system_model,
  indicatorId,
  nodeIds,
}: FetchNodeScoresParams): Promise<Map<string, number | null>> => {
  const responses = await Promise.all(
    chunk(nodeIds, NODE_SCORES_CHUNK_SIZE).map((node_ids) =>
      api.post<V4NodeScoresResponse>(
        '/web/v4/nodes/scores',
        { indicator_id: indicatorId, node_ids },
        { params: { version, system_model } }
      )
    )
  );

  const scoreByNodeId = new Map<string, number | null>();
  for (const { data } of responses) {
    for (const { node_id, score } of data.scores) scoreByNodeId.set(node_id, score);
  }
  return scoreByNodeId;
};

export const nodeScoresQueryKey = (version: string, system_model: string, indicatorId: string, sortedIds: string[]) => [
  'v4NodeScores',
  version,
  system_model,
  indicatorId,
  ...sortedIds,
];

const EMPTY_SCORES: Map<string, number | null> = new Map();

export type V4NodeScoresResult = {
  scoreByNodeId: Map<string, number | null>;
  isLoading: boolean;
  isError: boolean;
};

const useV4NodeScores = (nodeIds: string[], indicatorId: string): V4NodeScoresResult => {
  const { ver: version, system_model } = useVersionInfo();
  const ids = useMemo(() => normalizeNodeIds(nodeIds), [nodeIds]);
  const sortedIds = useMemo(() => [...ids].sort(), [ids]);

  const query = useQuery<Map<string, number | null>, AxiosError>({
    queryKey: nodeScoresQueryKey(version, system_model, indicatorId, sortedIds),
    queryFn: () => fetchNodeScores({ version, system_model, indicatorId, nodeIds: ids }),
    enabled: !!indicatorId && ids.length > 0,
    staleTime: Infinity,
    retry: false,
  });

  return {
    scoreByNodeId: query.data ?? EMPTY_SCORES,
    isLoading: query.isLoading,
    isError: query.isError,
  };
};

export default useV4NodeScores;
